import React from 'react';
import { Send, Mail, Clock, Globe } from 'lucide-react';
import type { GatheredEmailsResponse } from '../types';

interface CampaignSummaryProps {
  emailsData: GatheredEmailsResponse;
  onDispatch: () => void;
}

export default function CampaignSummary({ emailsData, onDispatch }: CampaignSummaryProps) {
  // Count countries if available
  const countryCount = emailsData.countryDistribution 
    ? Object.keys(emailsData.countryDistribution).length 
    : 0;

  return (
    <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Campaign Summary</h2>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 p-4 rounded-md">
            <div className="flex items-center mb-2">
              <Mail className="h-5 w-5 text-blue-500 mr-2" />
              <h3 className="font-medium">Email Distribution</h3>
            </div>
            <p className="text-gray-600 mb-2">
              <span className="font-semibold text-blue-700">{emailsData.gatheredEmails.length}</span> emails will be sent
            </p>
            <div className="text-xs text-gray-500 max-h-32 overflow-y-auto">
              {Object.entries(emailsData.domainDistribution)
                .sort((a, b) => b[1] - a[1]) // Sort by count descending
                .slice(0, 10) // Show top 10 domains
                .map(([domain, count]) => (
                <div key={domain} className="flex justify-between mb-1">
                  <span>@{domain}</span>
                  <span>{count} emails</span>
                </div>
              ))}
              {Object.keys(emailsData.domainDistribution).length > 10 && (
                <div className="text-xs text-center text-blue-500 mt-1">
                  + {Object.keys(emailsData.domainDistribution).length - 10} more domains
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-md">
            <div className="flex items-center mb-2">
              <Clock className="h-5 w-5 text-green-500 mr-2" />
              <h3 className="font-medium">Campaign Details</h3>
            </div>
            <p className="text-gray-600 mb-2">
              <span className="font-medium">Enhanced Description:</span>
            </p>
            <p className="text-gray-700 text-sm">
              {emailsData.enhancedDescription}
            </p>
            
            {emailsData.countryDistribution && (
              <div className="mt-3">
                <div className="flex items-center mb-1">
                  <Globe className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-xs font-medium text-gray-600">Geographic Reach: {countryCount} countries</span>
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs mt-1">
                  {Object.entries(emailsData.countryDistribution)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 6)
                    .map(([country, count]) => (
                    <div key={country} className="flex justify-between">
                      <span>{country}:</span>
                      <span>{count} emails</span>
                    </div>
                  ))}
                </div>
                {Object.keys(emailsData.countryDistribution).length > 6 && (
                  <div className="text-xs text-center text-green-600 mt-1">
                    + {Object.keys(emailsData.countryDistribution).length - 6} more countries
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="font-medium">Email Status</h3>
          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-blue-700">
              <strong>Note:</strong> {emailsData.testEmailStatus}
            </p>
            <p className="text-blue-700 text-xs mt-1">
              Emails will be sent using Supabase Edge Functions with the Gmail account credentials.
            </p>
          </div>
        </div>
        
        <div>
          <h3 className="font-medium">Notes</h3>
          <p className="text-gray-600">{emailsData.notes}</p>
        </div>

        <button
          onClick={onDispatch}
          className="w-full bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600 flex items-center justify-center gap-2"
        >
          <Send className="h-5 w-5" />
          Dispatch Campaign
        </button>
      </div>
    </div>
  );
}